/**
 * You › Notifications › Response time: p50/p95 from a run becoming blocked to you knowing, per
 * stage of the path (agent → hub → push → shown). Every row drills into its slowest moments and
 * their stage breakdown, so a slow p95 can be traced to the stage responsible.
 */
import { useMemo } from 'react'
import {
  duration,
  formatMs,
  KNOWN,
  type LatencyKind,
  type LatencySample,
  STAGES,
  type StageStat,
  summarize,
} from '../lib/latency.js'
import { useLatencySamples } from '../lib/latency-store.js'

const KIND_LABEL: Record<LatencyKind, string> = {
  question: 'Question',
  approval: 'Approval',
  landing: 'Landing',
  alert: 'Device alert',
  other: 'Other',
}

function Breakdown({ sample }: { sample: LatencySample }) {
  return (
    <ul className="m-0 list-none p-0 text-[14px] text-text-2">
      {[KNOWN, ...STAGES].map((d) => {
        const ms = duration(sample.stamps, d)
        return ms === undefined ? null : (
          <li key={d.key}>
            {d.label}: <b className="text-text-1">{formatMs(ms)}</b>
          </li>
        )
      })}
    </ul>
  )
}

function StatRow({
  s,
  samples,
  strong,
}: {
  s: StageStat
  samples: Map<string, LatencySample>
  strong?: boolean
}) {
  return (
    <details className="border-0 border-t border-solid border-[var(--vk-border-subtle)] px-4 py-2">
      <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-4 gap-y-1 text-[15px]">
        <span className={`flex-1 ${strong ? 'font-semibold' : ''}`}>{s.label}</span>
        <span className="text-text-2">
          p50 <b className="text-text-1">{formatMs(s.p50)}</b>
        </span>
        <span className="text-text-2">
          p95 <b className="text-text-1">{formatMs(s.p95)}</b>
        </span>
        <span className="text-[14px] text-text-3">{s.n} samples</span>
      </summary>
      <p className="m-0 mt-2 text-[14px] text-text-3">{s.hint}</p>
      {s.slowest.length ? (
        <ol className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
          {s.slowest.map((r) => {
            const full = samples.get(r.id)
            return (
              <li key={r.id} className="text-[14px]">
                <details>
                  <summary className="cursor-pointer">
                    {KIND_LABEL[r.kind]} <b>{formatMs(r.ms)}</b>
                  </summary>
                  {full ? <Breakdown sample={full} /> : null}
                </details>
              </li>
            )
          })}
        </ol>
      ) : (
        <p className="m-0 mt-2 text-[14px] text-text-3">Nothing measured yet.</p>
      )}
    </details>
  )
}

export function LatencyStats() {
  const q = useLatencySamples()
  const now = Date.now()
  const samples = q.data ?? []
  const byId = useMemo(() => new Map(samples.map((s) => [s.id, s])), [samples])
  const sum = useMemo(() => summarize(samples, now), [samples, now])
  if (q.isPending) return null
  return (
    <div>
      <p className="m-0 px-4 pb-2 text-[14px] text-text-2">
        {sum.count
          ? `${sum.count} blocked moments in the last 14 days. Times come from your own devices and clocks.`
          : 'Nothing measured yet. Each time an agent needs you, Viko records how long it took you to find out.'}
      </p>
      <StatRow s={sum.known} samples={byId} strong />
      <StatRow s={sum.resolved} samples={byId} strong />
      {sum.stages.map((s) => (
        <StatRow key={s.key} s={s} samples={byId} />
      ))}
    </div>
  )
}
